/**
 * SUPPORT-ONLY. Not exposed to clients.
 *
 * Parses the `receiptData` object embedded in a public ChargeLab receipt page
 * (https://charge.id/receipts/<id>) into the bits the OCPP timelog tool needs:
 * the charger device id, the session time window, and a billing breakdown to
 * overlay on the timeline. Pure functions — no network (see fetchReceipt.ts).
 *
 * Ported from scripts/ocpp-log-visualizer demo (receiptApi.ts).
 */

const JOULES_PER_KWH = 3_600_000;

/** One billed line item (charging or idle), simplified for display/overlay. */
export interface ReceiptLineItem {
  kind: string; // unitPricingType, e.g. BILLED_BY_KWH | IDLE_RATE
  label: string; // human ("Charging" / "Idle")
  energyKwh?: number;
  /** Wall-clock span of the billed period (stopTime − startTime). */
  durationSeconds?: number;
  /** Epoch-ms bounds of the billed period, when the receipt carries them. */
  startTimeMs?: number;
  stopTimeMs?: number;
  unitPrice?: number;
  total?: number;
}

/**
 * The gold nuggets a receipt carries that OCPP logs don't: cost breakdown,
 * connector type, how the session started, charger hardware, site/operator.
 * Everything optional — receipts vary.
 */
export interface ReceiptSummary {
  deviceId?: string;
  charger?: string;
  vendor?: string;
  model?: string;
  level?: string;
  maxKw?: number;
  connectorId?: string;
  connectorType?: string;
  connectorLocation?: string;
  startedVia?: string;
  companyId?: string;
  operator?: string;
  location?: string;
  address?: string;
  timeZone?: string;
  currency?: string;
  total?: number;
  chargingAmount?: number;
  idleAmount?: number;
  amountPaid?: number; // dollars (the receipt carries cents)
  energyKwh?: number;
  chargingTimeSeconds?: number;
  idleTimeSeconds?: number;
  pluggedInTimeSeconds?: number;
  when?: string; // formattedReceiptDateTime
  transactionId?: string;
  lineItems: ReceiptLineItem[];
}

export interface ReceiptInfo {
  receiptId: string;
  deviceId: string;
  chargerName?: string;
  sessionStart: Date;
  sessionEnd: Date;
  summary: ReceiptSummary;
}

/**
 * Loose, partial shape of the `receiptData` object scraped from the public
 * receipt page. Every field is optional — receipts vary by environment and
 * version — so reads fall back gracefully. This is intentionally minimal: only
 * the fields we actually consume are declared.
 */
interface RawAddress {
  streetNumber?: string;
  street?: string;
  city?: string;
  stateOrRegion?: string;
  zipOrPostalCode?: string;
}
interface RawConnector {
  id?: string | number;
  type?: string;
  physicalLocation?: string;
}
interface RawLineItem {
  unitPricingType?: string;
  energyDelivered?: number;
  startTime?: number;
  stopTime?: number;
  unitPrice?: number;
  totalPrice?: number;
}
interface RawCreditingReportEvent {
  deviceId?: string;
  connectorId?: string | number;
  portId?: string | number;
  startedVia?: string;
  transactionId?: string;
  timestampMillis?: number;
  chargeFractions?: { beginTimestampMillis?: number }[];
}
interface RawPricingReport {
  creditingReportEvent?: RawCreditingReportEvent;
  chargingLineItems?: RawLineItem[];
  currencyCode?: string;
  amount?: number;
  chargingAmount?: number;
  idleAmount?: number;
  totalEnergyDeliveredJoules?: number;
  effectiveChargingTimeSeconds?: number;
  effectiveIdleTimeSeconds?: number;
  timestampMillis?: number;
}
interface RawFrontendInfo {
  displayName?: string;
}
export interface RawReceiptData {
  pricingReport?: RawPricingReport;
  info?: {
    deviceId?: string;
    vendor?: string;
    model?: string;
    level?: string;
    maxWattage?: number;
    connectors?: RawConnector[];
  };
  charger?: { id?: string; frontendInfo?: RawFrontendInfo };
  location?: { frontendInfo?: RawFrontendInfo; address?: RawAddress; timeZone?: string };
  company?: { id?: string; frontendInfo?: RawFrontendInfo };
  amountPaid?: number;
  formattedReceiptDateTime?: string;
  pluggedInTimeSeconds?: number;
}

/** The charger's deviceId, trying the few places a receipt stashes it. */
function pickDeviceId(data: RawReceiptData): string | undefined {
  return (
    data.info?.deviceId ?? data.charger?.id ?? data.pricingReport?.creditingReportEvent?.deviceId
  );
}

/** Pull the human-useful fields out of the raw receiptData object. */
function parseReceiptSummary(data: RawReceiptData): ReceiptSummary {
  const pr = data.pricingReport ?? {};
  const cre = pr.creditingReportEvent ?? {};
  const connectorId = cre.connectorId ?? cre.portId;
  const connector = (data.info?.connectors ?? []).find((c) => String(c.id) === String(connectorId));
  const a = data.location?.address;
  const address = a
    ? [a.streetNumber, a.street, a.city, a.stateOrRegion, a.zipOrPostalCode]
        .map((x) => (x ?? '').trim())
        .filter(Boolean)
        .join(', ')
    : undefined;

  const lineItems: ReceiptLineItem[] = (pr.chargingLineItems ?? []).map((li) => ({
    kind: li.unitPricingType ?? 'Item',
    label:
      li.unitPricingType === 'BILLED_BY_KWH'
        ? 'Charging'
        : li.unitPricingType === 'IDLE_RATE'
        ? 'Idle'
        : String(li.unitPricingType ?? 'Item'),
    energyKwh: li.energyDelivered ? li.energyDelivered / JOULES_PER_KWH : undefined,
    durationSeconds: li.startTime && li.stopTime ? (li.stopTime - li.startTime) / 1000 : undefined,
    startTimeMs: typeof li.startTime === 'number' ? li.startTime : undefined,
    stopTimeMs: typeof li.stopTime === 'number' ? li.stopTime : undefined,
    unitPrice: li.unitPrice,
    total: li.totalPrice,
  }));

  return {
    deviceId: pickDeviceId(data),
    charger: data.charger?.frontendInfo?.displayName,
    vendor: data.info?.vendor,
    model: data.info?.model,
    level: data.info?.level,
    // info.maxWattage is reported in kW already (e.g. 180 for a 180 kW DC charger).
    maxKw: typeof data.info?.maxWattage === 'number' ? data.info.maxWattage : undefined,
    connectorId: connectorId != null ? String(connectorId) : undefined,
    connectorType: connector?.type,
    connectorLocation: connector?.physicalLocation,
    startedVia: cre.startedVia,
    companyId: data.company?.id,
    operator: data.company?.frontendInfo?.displayName,
    location: data.location?.frontendInfo?.displayName,
    address,
    timeZone: data.location?.timeZone,
    currency: pr.currencyCode,
    total: pr.amount,
    chargingAmount: pr.chargingAmount,
    idleAmount: pr.idleAmount,
    amountPaid: typeof data.amountPaid === 'number' ? data.amountPaid / 100 : undefined,
    energyKwh:
      typeof pr.totalEnergyDeliveredJoules === 'number'
        ? pr.totalEnergyDeliveredJoules / JOULES_PER_KWH
        : undefined,
    chargingTimeSeconds: pr.effectiveChargingTimeSeconds,
    idleTimeSeconds: pr.effectiveIdleTimeSeconds,
    pluggedInTimeSeconds: data.pluggedInTimeSeconds,
    when: data.formattedReceiptDateTime,
    transactionId: cre.transactionId,
    lineItems,
  };
}

/** Parse a raw `receiptData` object into the device id, session window, and summary. */
export function parseReceiptData(receiptId: string, data: RawReceiptData): ReceiptInfo {
  const deviceId = pickDeviceId(data);
  if (!deviceId) throw new Error('receipt has no deviceId');

  // Session window: span of the charging line items, with sensible fallbacks.
  const lineItems = data.pricingReport?.chargingLineItems ?? [];
  const starts = lineItems
    .map((x) => x.startTime)
    .filter((n): n is number => typeof n === 'number');
  const stops = lineItems.map((x) => x.stopTime).filter((n): n is number => typeof n === 'number');
  const cre = data.pricingReport?.creditingReportEvent;
  const fractions = cre?.chargeFractions ?? [];

  const startMs = starts.length
    ? Math.min(...starts)
    : fractions[0]?.beginTimestampMillis ?? cre?.timestampMillis;
  const endMs = stops.length ? Math.max(...stops) : data.pricingReport?.timestampMillis;
  if (typeof startMs !== 'number' || typeof endMs !== 'number') {
    throw new Error('receipt has no usable session timing');
  }

  return {
    receiptId,
    deviceId,
    chargerName: data.charger?.frontendInfo?.displayName,
    sessionStart: new Date(startMs),
    sessionEnd: new Date(endMs),
    summary: parseReceiptSummary(data),
  };
}

/** Pull a balanced `{...}` object out of `<script>` source, starting at `marker`. */
export function extractJsonAfter(source: string, marker: string): string {
  const m = source.indexOf(marker);
  if (m < 0) throw new Error(`could not find "${marker}" in the receipt page`);
  let i = source.indexOf('{', m);
  if (i < 0) throw new Error('no JSON object found after the marker');
  const start = i;
  let depth = 0;
  let inStr = false;
  let esc = false;
  for (; i < source.length; i++) {
    const ch = source[i];
    if (inStr) {
      if (esc) esc = false;
      else if (ch === '\\') esc = true;
      else if (ch === '"') inStr = false;
    } else if (ch === '"') {
      inStr = true;
    } else if (ch === '{') {
      depth++;
    } else if (ch === '}') {
      depth--;
      if (depth === 0) return source.slice(start, i + 1);
    }
  }
  throw new Error('unterminated JSON object in the receipt page');
}
